import {
  adjustmentReasonLabel,
  type InventoryRow,
  type StockAdjustment,
  unavailableTotal,
} from "@estelle/domain";

/** A row flattened to the field names its module declares. */
export type Projection = Record<string, unknown>;

/**
 * An inventory row flattened for display.
 *
 * Deliberately computed from the row and nothing else — see the comment atop
 * `modules/orders/projections.ts`. The adapter's own projection (used for
 * filtering and sorting) can be richer than this one, because it holds the
 * whole dataset and can join what the row does not carry.
 *
 * The consequence here is `vendor`: it *is* on `row.product`, so it renders
 * fine, but `productType` and `tags` are not on `row.product` at all
 * (`InventoryRow.product` is `Pick<Product, "id" | "title" | "vendor" |
 * "featuredImageUrl">`) — there is nothing in this function's argument to
 * read them from. They stay declared in `INVENTORY_FIELDS` for FR-INV-4 as
 * filter-only fields (`displayable: false`, #503), so nothing asks this
 * function for them — the same gap `hasInvoice` leaves in the Orders module.
 */
export function projectInventoryRow(row: InventoryRow): Projection {
  return {
    productTitle: row.product.title,
    variantTitle: row.variant.title,
    sku: row.variant.sku ?? "",
    vendor: row.product.vendor ?? "",
    warehouse: row.warehouse.code,
    available: row.available,
    committed: row.committed,
    unavailable: unavailableTotal(row.unavailable),
    onHand: row.onHand,
    // FR-INV-2: shown as its own field rather than folded into `available`,
    // because a warehouse operator asking "how much can I ship from here
    // right now" and one asking "how much is on its way" are asking two
    // different questions, and a single combined figure answers neither.
    inTransit: row.inTransit,
    hsnCode: row.variant.hsnCode ?? "",
    // FR-INV-5. This is when `available` (and `committed`, where the channel
    // reports it) last came back from the channel — `inTransit` has no sync
    // time of its own because nothing sync-worthy produced it: it is derived
    // from the ERP's own transfers, not read back from Shopify.
    lastSyncedAt: row.lastSyncedAt,
    lowStockThreshold: row.variant.lowStockThreshold,
    // Judged by the threshold the server used (`effectiveLowStockThreshold`),
    // not by a default assumed here — the default is a deployment setting.
    belowThreshold: row.available <= row.effectiveLowStockThreshold,
    outOfStock: row.available <= 0,
  };
}

/**
 * A stock adjustment flattened for display.
 *
 * `variantId` and `warehouseId` stay as raw ids here for the same reason
 * `projectInventoryRow` cannot fill in `productType` — `StockAdjustment` is
 * not joined to a variant or a warehouse the way `InventoryRow` is.
 * `Adjustments.tsx` resolves them to a SKU and a warehouse code with its own
 * lookup for display; this function has nothing to resolve them with.
 */
export function projectAdjustment(adjustment: StockAdjustment): Projection {
  return {
    createdAt: adjustment.createdAt,
    // The words, not the stored code: "RTO received", where a generic
    // humanising of `rto_received` gives "Rto received". The list draws it and
    // an export writes it; the filter still matches the stored code (the
    // adapters filter on their own projections).
    reason: adjustmentReasonLabel(adjustment.reason),
    delta: adjustment.delta,
    status: adjustment.status,
    actorUserId: adjustment.actorUserId,
    error: adjustment.error ?? "",
    variantId: adjustment.variantId,
    warehouseId: adjustment.warehouseId,
    note: adjustment.note ?? "",
  };
}
